<template>
  <AppBody :subtitle="$t('pos.title')" :actions="actions">
    <CardModule untitle>
      <ExpansionPanelFilters>
        <v-form @submit.prevent="filterPointsOfSale" class="search-forms">
          <v-row>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.name"
                :label="$t('pos.representative-name')"
                :placeholder="$t('pos.insert-name')"
                data-cy="representante"
                clearable
              ></BaseTextField>
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.cpf"
                label="CPF"
                :placeholder="$t('pos.insert-cpf-number')"
                data-cy="cpf"
                v-mask="'###.###.###-##'"
                clearable
              ></BaseTextField>
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.cnpj"
                label="CNPJ"
                :placeholder="$t('pos.insert-cnpj-number')"
                data-cy="cnpj"
                v-mask="'##.###.###/####-##'"
                clearable
              ></BaseTextField>
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseSelect
                v-model="filter.status"
                label="Status"
                :placeholder="$t('pos.select-status')"
                :persistent-placeholder="false"
                :items="pointsOfSaleStatus"
                item-text="value"
                item-value="key"
                clearable
              ></BaseSelect>
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseSelect
                v-model="filter.personType"
                :label="$t('pos.type-of-person')"
                :placeholder="$t('pos.select-type-of-person')"
                :persistent-placeholder="false"
                :items="personTypeOptions"
                item-text="text"
                item-value="value"
                clearable
              ></BaseSelect>
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <DatePicker
                :label="$t('pos.initial-inclusion-date')"
                placeholder="dd/mm/aaaa"
                @formatted-date="handleInitialDate"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <DatePicker
                :label="$t('pos.final-inclusion-date')"
                placeholder="dd/mm/aaaa"
                @formatted-date="handleFinalDate"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.email"
                label="E-mail"
                :placeholder="$t('pos.insert-email')"
                data-cy="email"
                clearable
              >
              </BaseTextField>
            </v-col>
            <v-col class="d-flex justify-end">
              <BaseButton variant="filter" type="submit" :loading="posLoading">
                {{ $t("common.filter") }}
              </BaseButton>
            </v-col>
          </v-row>
        </v-form>
      </ExpansionPanelFilters>

      <v-data-table
        class="table-body"
        :no-data-text="$t('common.no-results-found')"
        :loading-text="$t('common.searching-info')"
        mobile-breakpoint="0"
        :loading="posLoading"
        :headers="headers"
        :items="pointsOfSale"
        :items-per-page="10"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50],
          itemsPerPageText: $t('pos.pos-per-page'),
        }"
      >
        <template v-slot:item.representante_nome="{ item }">
          {{ item.razao_social ? item.razao_social : item.representante_nome }}
        </template>
        <template v-slot:item.ins_dthr="{ item }">
          {{ formatDate(item.ins_dthr) }}
        </template>
        <template v-slot:item.tipo_pessoa="{ item }">
          {{ item.tipo_pessoa ? item.tipo_pessoa : "-" }}
        </template>
        <template v-slot:item.representante_cpf="{ item }">
          {{
            item.tipo_pessoa === "FISICA" ? item.representante_cpf : item.cnpj
          }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="formatStatus(item.status).color">
            {{ formatStatus(item.status).text }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div style="display: flex; justify-content: center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  :color="$config.dados.corinputs"
                  :loading="item.edit_button_loading"
                  @click="editPos(item)"
                  height="30"
                  width="30"
                  small
                  icon
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("common.edit") }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </CardModule>
    <ModalConfirmacao
      :textModal="$pointOfSale.text"
      @callback="$pointOfSale.removeTrigger"
    />
  </AppBody>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { formatDate, formatStatus } from "../../utils";
import AppBody from "../../layouts/components/AppBody.vue";
import CardModule from "../../components/cards/CardModule.vue";
import ExpansionPanelFilters from "../../components/forms/ExpansionPanelFilters.vue";
import BaseTextField from "../../components/forms/BaseTextField.vue";
import BaseSelect from "../../components/forms/BaseSelect.vue";
import BaseButton from "../../components/forms/BaseButton.vue";
import DatePicker from "../../components/forms/DatePicker.vue";

export default {
  name: "PointOfSale",
  metaInfo() {
    const title = this.$t("pos.title");
    return {
      title: `${title} | ElevenTickets`,
      meta: [
        {
          property: "og:title",
          content: `${title} | ElevenTickets`,
        },
      ],
    };
  },
  components: {
    AppBody,
    CardModule,
    BaseTextField,
    BaseSelect,
    BaseButton,
    ModalConfirmacao: () => import("@/components/global/modal-confirmacao.vue"),
    ExpansionPanelFilters,
    DatePicker,
  },
  data() {
    return {
      formatDate,
      formatStatus,
      filter: {
        name: "",
        cpf: "",
        cnpj: "",
        status: "",
        personType: "",
        email: "",
        initialDate: "",
        finalDate: "",
      },
      actions: [
        {
          name: this.$t("pos.new-pos"),
          action: () =>
            this.$router.push({
              name: "pointsOfSaleSettings",
              params: { type: "cadastrar" },
            }),
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["pointsOfSale", "pointsOfSaleStatus", "posLoading"]),
    headers() {
      return [
        {
          text: this.$t("pos.point-of-sale"),
          align: "start",
          sortable: true,
          value: "representante_nome",
          width: 160,
        },
        {
          text: this.$t("pos.representative"),
          align: "start",
          sortable: true,
          value: "representante_nome",
          width: 140,
        },
        {
          text: this.$t("pos.register-date"),
          align: "start",
          sortable: true,
          value: "ins_dthr",
          width: 140,
        },
        {
          text: this.$t("pos.type-of-person"),
          align: "start",
          sortable: true,
          value: "tipo_pessoa",
          width: 140,
        },
        {
          text: "CPF/CNPJ",
          align: "start",
          sortable: true,
          value: "representante_cpf",
          width: 140,
        },
        {
          text: "E-mail",
          align: "start",
          sortable: true,
          value: "representante_email",
          width: 100,
        },
        {
          text: "Status",
          align: "center",
          sortable: true,
          value: "status",
          width: 100,
        },
        {
          text: this.$t("common.actions"),
          align: "center",
          sortable: false,
          value: "actions",
          width: 100,
        },
      ];
    },
    personTypeOptions() {
      return [
        {
          text: this.$t("pos.legal"),
          value: "JURIDICA",
        },
        {
          text: this.$t("pos.natural"),
          value: "FISICA",
        },
      ];
    },
  },
  methods: {
    ...mapActions(["fetchPointsOfSale", "fetchPosStatus", "getPosData"]),
    filterPointsOfSale() {
      const filters = [];

      if (this.filter.name) {
        filters.push({
          campo: "representante_nome",
          orientacao: "like",
          tipo: "string",
          valor: this.filter.name.trim(),
        });
      }
      if (this.filter.cpf) {
        filters.push({
          campo: "representante_cpf",
          orientacao: "like",
          tipo: "string",
          valor: this.filter.cpf,
        });
      }
      if (this.filter.cnpj) {
        filters.push({
          campo: "cnpj",
          orientacao: "like",
          tipo: "string",
          valor: this.filter.cnpj,
        });
      }
      if (this.filter.status) {
        filters.push({
          campo: "status",
          tipo: "string",
          valor: this.filter.status,
        });
      }
      if (this.filter.personType) {
        filters.push({
          campo: "tipo_pessoa",
          orientacao: "like",
          tipo: "string",
          valor: this.filter.personType,
        });
      }
      if (this.filter.initialDate) {
        filters.push({
          campo: "ins_dthr",
          orientacao: "maior",
          tipo: "date",
          valor: this.filter.initialDate + " 00:00",
        });
      }
      if (this.filter.finalDate) {
        filters.push({
          campo: "ins_dthr",
          orientacao: "menor",
          tipo: "date",
          valor: this.filter.finalDate + " 23:59",
        });
      }
      if (this.filter.email) {
        filters.push({
          campo: "representante_email",
          orientacao: "like",
          tipo: "string",
          valor: this.filter.email.trim(),
        });
      }

      this.fetchPointsOfSale(filters);
    },
    handleInitialDate(formattedDate) {
      this.filter.initialDate = formattedDate;
    },
    handleFinalDate(formattedDate) {
      this.filter.finalDate = formattedDate;
    },
    editPos(item) {
      this.getPosData(item);
      this.$router.push({
        name: "pointsOfSaleSettings",
        params: { type: "editar" },
      });
    },
  },
  mounted() {
    this.fetchPointsOfSale();
    this.fetchPosStatus();
  },
};
</script>

<style scoped></style>
